import React, { useContext, useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { AppContext } from '../../App';
import { getProjectSelectList } from '../../clients/project_client';
import { requestOrderFromProduct } from '../../clients/order_request_client';
import { getUserSelectList } from '../../clients/user_client';
import DropdownSelect from '../Generic/DropdownSelect';
import { showToast } from '../../config_and_helpers/helpers';
import { logInfo } from '../../config_and_helpers/logger';
import { findUserOption } from '../../config_and_helpers/userDropdownOptions';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';

const LOG_CONTEXT = 'InventoryRequestOrderPanel';

/**
 * Request procurement to order an inventory product (OrderRequest record, no Quote/Order).
 * Renders a trigger button; the form lives in a Bootstrap modal.
 *
 * Optional related user from the org user list (`contact_user_id`); when set,
 * backend derives contact name/email from the selected user profile.
 *
 * @param {Object} props
 * @param {Object} props.product - Product with id and supplier_cat_item
 * @param {boolean} [props.compact=false] - Render a compact trigger for modal headers.
 */
const InventoryRequestOrderPanel = ({ product, compact = false }) => {
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();
  const [show, setShow] = useState(false);
  const [quantity, setQuantity] = useState('');
  const [project, setProject] = useState('');
  const [notes, setNotes] = useState('');
  const [contactUserId, setContactUserId] = useState('');
  const [projectOptions, setProjectOptions] = useState([]);
  const [userSelectList, setUserSelectList] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (token) {
      getProjectSelectList(token, setProjectOptions);
    }
  }, [token]);

  useEffect(() => {
    if (!show || !token) return;
    setIsLoadingUsers(true);
    getUserSelectList(token, setUserSelectList).finally(() => {
      setIsLoadingUsers(false);
    });
  }, [show, token]);

  const resetForm = () => {
    setQuantity('');
    setProject('');
    setNotes('');
    setContactUserId('');
    setErrors([]);
  };

  const handleClose = () => {
    setShow(false);
    resetForm();
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setErrors([]);

    const qty = Number(quantity);
    if (!Number.isInteger(qty) || qty < 1) {
      setErrors(['Quantity must be an integer of at least 1.']);
      return;
    }

    const payload = { quantity: qty };
    const userId = Number(contactUserId);
    if (Number.isInteger(userId) && userId > 0) {
      payload.contact_user_id = userId;
    }
    if (project) payload.project = Number(project);
    if (notes.trim()) payload.notes = notes.trim();

    setIsSubmitting(true);
    logInfo(LOG_CONTEXT, 'submit order request', {
      productId: product.id,
      contactUserId: payload.contact_user_id ?? null,
    });

    const response = await requestOrderFromProduct(token, product.id, payload);
    setIsSubmitting(false);

    if (response?.success) {
      const ref = response.data?.request_ref || '';
      showToast(
        `Order request ${ref} submitted`,
        'success',
        'top-right',
        5000
      );
      handleClose();
    } else {
      setErrors(
        response?.messages?.length
          ? response.messages
          : ['Request failed. Please try again.']
      );
    }
  };

  if (isViewer) {
    return null;
  }

  return (
    <>
      <Button
        variant="primary"
        size={compact ? 'sm' : undefined}
        className={compact ? 'flex-shrink-0' : 'mb-3'}
        onClick={() => setShow(true)}
      >
        Request order
      </Button>

      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Request order from procurement</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <p className="text-muted small mb-3">
              Sends an internal order request to procurement. This does not
              create a quote or order in Materiah.
            </p>
            {errors.length > 0 ? (
              <Alert variant="danger">
                {errors.map((msg, i) => (
                  <div key={i}>{msg}</div>
                ))}
              </Alert>
            ) : null}
            <Form.Group className="mb-2">
              <Form.Label>Quantity</Form.Label>
              <Form.Control
                type="number"
                min={1}
                value={quantity}
                onChange={e => setQuantity(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <DropdownSelect
                optionsList={userSelectList}
                label="Related User"
                selectedValue={findUserOption(userSelectList, contactUserId)}
                setSelectedValue={opt => setContactUserId(opt?.value ?? '')}
                disabled={isLoadingUsers}
                clearable
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Project</Form.Label>
              <Form.Select
                value={project}
                onChange={e => setProject(e.target.value)}
              >
                <option value="">None</option>
                {projectOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-0">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={notes}
                onChange={e => setNotes(e.target.value)}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Submitting…
                </>
              ) : (
                'Submit request'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default InventoryRequestOrderPanel;
